import useQueryParams from '@/hooks/useQueryParams';

import bitriseCIIntentIllustration from './assets/bitrise-ci.png';
import buildCacheIntentIllustration from './assets/build-cache.png';
import buildHubIntentIllustration from './assets/build-hub.png';
import releaseManagementIntentIllustration from './assets/release-management.png';
import remoteDevEnvironmentsIntentIllustration from './assets/remote-development-environments.svg';

const PRODUCT_INTENT_QUERY_PARAM = 'product';

export type ProductIntentConfiguration = {
  name: string;
  title: string;
  features: string[];
  illustration: string;
  paymentNotice?: string;
  destinationPath: string;
};

export const productIntentConfigurations: Record<string, ProductIntentConfiguration> = {
  ci: {
    name: 'Bitrise CI',
    title: 'Sign up, start building with',
    features: [
      'Connect your repo and add your first project',
      'Generate a workflow and run your first build',
      'Activate your 30-day free trial of Bitrise Pro',
    ],
    paymentNotice: "Credit card verification is required for the trial. We won't charge you when your trial ends.",
    illustration: bitriseCIIntentIllustration,
    destinationPath: '/ci',
  },
  'build-hub': {
    name: 'Build Hub',
    title: 'Sign up, start running builds on',
    features: [
      '14-day free trial',
      'Run your existing GitHub Actions jobs without any changes: just connect and go',
      'M4 Apple Silicon and high-core Linux machines',
      'Quick Builds start, no queues, no cold starts',
      '100 GB build cache included',
    ],
    illustration: buildHubIntentIllustration,
    destinationPath: '/build-hub',
  },
  'build-cache': {
    name: 'Build Cache',
    title: 'Sign up, start saving time with',
    features: [
      '30-day full-featured trial',
      'Reduce build and test durations up to 90%',
      'Works with any CI/CD including Bitrise, Circle CI & GitHub Actions',
      'Support for Gradle, Xcode, Bazel and React Native projects',
    ],
    paymentNotice: 'No credit card required for the trial',
    illustration: buildCacheIntentIllustration,
    destinationPath: '/build-cache',
  },
  'release-management': {
    name: 'Release Management',
    title: 'Sign up and distribute your apps with',
    features: [
      'Distribute test builds to iOS and Android devices',
      'Manage App Store and Google Play releases',
      'Deploy CodePush updates to React Native apps',
    ],
    paymentNotice: 'Start for free with a Basic plan',
    illustration: releaseManagementIntentIllustration,
    destinationPath: '/release-management',
  },
  'remote-development-environments': {
    name: 'Remote Dev Environment',
    title: 'Sign up, spin up your first',
    features: [
      '14-day free trial',
      'M2 Pro Apple Silicon and x86 Linux dev machines, ready in seconds (M4 and M4 Pro available on Enterprise)',
      'Spin up in seconds, archive when done, and pick up where you left off',
      'Run AI agents like Claude Code or Codex in secure cloud VMs',
      'Connect with VS Code, Cursor, terminal, or VNC',
    ],
    illustration: remoteDevEnvironmentsIntentIllustration,
    destinationPath: '/dev-environments',
  },
  codepush: {
    name: 'CodePush',
    title: 'Sign up and ship instant OTA updates with',
    features: [
      'Push JavaScript and asset updates straight to users, no app store review.',
      'Delta Updates on by default, cutting update sizes by up to 90%.',
      'Code signing secures every update bundle before it reaches your users.',
    ],
    paymentNotice: 'Start for free with 100k MAU',
    illustration: releaseManagementIntentIllustration,
    destinationPath: '/release-management',
  },
};

export type ProductIntent = keyof typeof productIntentConfigurations;

const useProductIntent = (paramVariant?: ProductIntent): ProductIntent | undefined => {
  const queryParams: URLSearchParams = useQueryParams();
  const productIntentParam = queryParams.get(PRODUCT_INTENT_QUERY_PARAM);

  if (paramVariant && productIntentConfigurations[paramVariant]) {
    return paramVariant as ProductIntent;
  }

  if (productIntentParam && productIntentConfigurations[productIntentParam as ProductIntent]) {
    return productIntentParam as ProductIntent;
  }

  return undefined;
};

export default useProductIntent;
